import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Collapse } from 'reactstrap';
import { useMe } from '../auth/MeContext';
import { setCurrentWarehouse } from '../auth/currentWarehouse';
import { useRole } from '../auth/useRole';
import { useTenantContext } from '../auth/useTenantContext';
import { useFulfilledCount } from '../api/pickListLifecycle';
import { WarehouseSelector } from '../components/header/WarehouseSelector';
import { LanguageSwitcher } from '../components/header/LanguageSwitcher';
import { ThemeToggle } from '../components/header/ThemeToggle';
import { menuForRoles, type MenuItem } from './menu';
import { MenuBadge } from './MenuBadge';

/**
 * SP-8a — Header con menu orizzontale.
 *
 * Layout:
 *   [logo VP] [voci menu inline] ......... [warehouse] [theme] [lang] [bell] [avatar]
 *
 * Voci "platform" (Tenants, Item Schemas, Dialog Templates, Audit globale,
 * Voice Config) sono mostrate in un dropdown "Piattaforma ▾" perché sono
 * cinque, usate solo da platform-admin.
 *
 * Su mobile (<992px): le voci collassano in un menu hamburger che si apre
 * come overlay sotto l'header (toggle `mobileNavOpen` state).
 */
export function Header() {
  const { t } = useTranslation(['shell', 'common']);
  const { roles, isPlatformAdmin, isTenantAdmin, isWarehouseAdmin, isVoiceSupervisor } = useRole();
  const { state } = useMe();
  const auth = useAuth();
  const { token, tenantCode } = useTenantContext();

  const [userOpen, setUserOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const me = state.kind === 'ok' ? state.me : null;
  const initials = me ? 'OP' : '··';

  const warehouses: Array<{ id: number; code: string; name: string; is_default: boolean }> =
    state.kind === 'ok'
      ? ((state.me as Record<string, unknown>).warehouses as Array<{ id: number; code: string; name: string; is_default: boolean }> | undefined) ?? []
      : [];
  const showSelector = (isWarehouseAdmin || isVoiceSupervisor) && warehouses.length > 1;
  // SP-6g fix: senza `is_default` (default_warehouse null) `me.warehouse` è null;
  // ripieghiamo sul primo grant così la label statica del magazzino non resta vuota
  // (coerente col fallback di warehouseId in useTenantContext).
  const warehouseCode = me?.warehouse?.code ?? warehouses[0]?.code ?? null;

  const [currentId, setCurrentId] = useState<number | null>(() => {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('vp.current_warehouse_id') : null;
    if (raw) return Number(raw);
    const def = state.kind === 'ok'
      ? ((state.me as Record<string, unknown>).default_warehouse as { id: number } | null | undefined)?.id ?? null
      : null;
    return def;
  });

  function handleWarehouseChange(whId: number) {
    // Memorizza la scelta e RICARICA la pagina: invalidare la cache non basta,
    // perché `useTenantContext` legge il magazzino da localStorage durante il
    // render e le pagine non ri-renderizzano (vedi `setCurrentWarehouse`).
    setCurrentId(whId);
    setCurrentWarehouse(whId);
  }

  // Pick-list fulfilled badge: visibile a tenant-admin e warehouse-admin.
  const warehouseId = state.kind === 'ok' ? (state.me.warehouse?.id ?? null) : null;
  const fulfilled = useFulfilledCount(token, tenantCode, warehouseId);
  const fulfilledCount = (isTenantAdmin || isWarehouseAdmin) ? (fulfilled.data ?? 0) : 0;

  const allItems = menuForRoles(roles, isPlatformAdmin);
  const mainItems = allItems.filter((it) => !it.platform);
  const platformItems = allItems.filter((it) => it.platform);

  function renderMenuLink(it: MenuItem, onClick?: () => void) {
    return (
      <NavLink
        key={it.path}
        to={it.path}
        end={it.path === '/'}
        onClick={onClick}
        className={({ isActive }) =>
          'vp-topnav__item d-inline-flex align-items-center gap-1 px-2 py-1 rounded text-decoration-none' +
          (isActive ? ' vp-topnav__item--active' : '')
        }
      >
        <i className={`bi ${it.icon}`} />
        <span>{t(it.label)}</span>
        {it.path === '/pick-lists' && (
          <MenuBadge
            count={fulfilledCount}
            title={`${fulfilledCount} liste da confermare nel warehouse corrente`}
          />
        )}
      </NavLink>
    );
  }

  return (
    <header className="vp-topbar">
      <div className="navbar-header d-flex align-items-center justify-content-between">
        {/* Sinistra: brand + menu orizzontale (desktop) */}
        <div className="d-flex align-items-center gap-3 flex-grow-1">
          <button
            type="button"
            className="btn btn-sm vp-mobile-nav-toggle d-lg-none"
            aria-label="Apri/chiudi menu"
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <i className="bi bi-list fs-20" />
          </button>

          <NavLink to="/" className="d-flex align-items-center text-decoration-none" aria-label={t('shell:brand')}>
            <img src="/brand/wordmark-dark.png" alt={t('shell:brand')}
                 className="vp-brand-logo vp-brand-logo--light" />
            <img src="/brand/wordmark-light.png" alt=""
                 className="vp-brand-logo vp-brand-logo--dark" aria-hidden="true" />
          </NavLink>

          {/* Desktop menu */}
          <nav className="vp-topnav d-none d-lg-flex align-items-center gap-1">
            {mainItems.map((it) => renderMenuLink(it))}
            {platformItems.length > 0 && (
              <Dropdown isOpen={platformOpen} toggle={() => setPlatformOpen((o) => !o)}>
                <DropdownToggle
                  tag="button"
                  type="button"
                  className="vp-topnav__item d-inline-flex align-items-center gap-1 px-2 py-1 rounded bg-transparent border-0"
                  aria-label={t('shell:groups.platform')}
                >
                  <i className="bi bi-buildings" />
                  <span>{t('shell:groups.platform')}</span>
                  <i className="bi bi-chevron-down fs-12" />
                </DropdownToggle>
                <DropdownMenu>
                  {platformItems.map((it) => (
                    <DropdownItem key={it.path} tag={NavLink} to={it.path}>
                      <i className={`bi ${it.icon} me-2`} />
                      {t(it.label)}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            )}
          </nav>
        </div>

        {/* Destra: warehouse + tema + lingua + bell + avatar */}
        <div className="d-flex align-items-center gap-2 vp-header-utility">
          {!isPlatformAdmin && showSelector && (
            <WarehouseSelector
              warehouses={warehouses}
              currentId={currentId}
              onChange={handleWarehouseChange}
            />
          )}
          {!isPlatformAdmin && !showSelector && warehouseCode && (
            <span
              className="d-inline-flex align-items-center gap-1 text-muted px-2"
              title="Single warehouse — non interattivo"
            >
              <i className="bi bi-building" />
              <span className="font-monospace fw-medium d-none d-sm-inline">{warehouseCode}</span>
            </span>
          )}

          {/* Co-brand Sintesi Software (link al sito), tra selettore magazzino e toggle tema */}
          <a href="https://sintesisoftware.it" target="_blank" rel="noopener noreferrer"
             className="d-flex align-items-center" aria-label="Sintesi Software — sito web">
            <img src="/brand/logo-sintesi.svg" alt="Sintesi Software" className="vp-cobrand-logo" />
          </a>

          <span className="vr mx-1 d-none d-sm-inline" />

          <ThemeToggle />
          <LanguageSwitcher />

          <button className="btn btn-icon btn-sm btn-ghost-secondary" aria-label="Notifiche">
            <i className="bi bi-bell fs-18" />
          </button>

          <Dropdown isOpen={userOpen} toggle={() => setUserOpen((o) => !o)}>
            <DropdownToggle
              tag="button"
              type="button"
              className="btn btn-sm d-inline-flex align-items-center gap-2 shadow-none bg-transparent border-0"
              aria-label={`Utente ${me?.role.applicative ?? ''}`}
            >
              <span className="vp-avatar">{initials}</span>
              <i className="bi bi-chevron-down text-muted fs-12 d-none d-sm-inline" />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem header className="text-truncate">
                {me?.role.applicative ?? 'Utente'}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={() => void auth.signoutRedirect()}>
                <i className="bi bi-box-arrow-right me-2" />
                Logout
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      {/* Mobile menu overlay (sotto 992px) */}
      <Collapse isOpen={mobileNavOpen} className="d-lg-none">
        <nav className="vp-topnav-mobile d-flex flex-column">
          {mainItems.map((it) => renderMenuLink(it, () => setMobileNavOpen(false)))}
          {platformItems.length > 0 && (
            <>
              <div className="text-uppercase fw-medium text-muted small px-2 pt-2">
                {t('shell:groups.platform')}
              </div>
              {platformItems.map((it) => renderMenuLink(it, () => setMobileNavOpen(false)))}
            </>
          )}
        </nav>
      </Collapse>
    </header>
  );
}
